import { useCallback, useEffect, useState, useTransition } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import useArrivalOrderPolling from '@/hooks/useArrivalOrderPolling';
import { usePOLabelAsync } from '@/hooks/usePOLabelAsync';
import useServerPagination from '@/hooks/useServerPagination';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { RootState } from '@/redux/store';
import {
  Order as ArrivalOrder,
  IResArrivalOrder,
  LotBreakdownRowState,
} from '@/types/fulfill/inventoryHistory';
import { getPDF } from '@/utils/common';

import { convertArrayToQueryString } from '../../OrderStatus/Table/utils';

export interface IInventoryHistoryFilters {
  inventoryStatus: string[];
  status: string[];
  startDate?: string;
  endDate?: string;
  [key: string]: string | string[] | undefined;
}

interface Order {
  data: IResArrivalOrder['orders'];
  loading: boolean;
  error: boolean;
  totalRows: number;
}

const initialOrdersState: Order = {
  data: [],
  loading: false,
  error: false,
  totalRows: 0,
};
const useInventoryHistory = () => {
  const [orders, setOrders] = useState<Order>(initialOrdersState);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [search, setSearch] = useState<string>('');
  const [selectedOrder, setSelectedOrder] = useState<ArrivalOrder | null>(null);
  const [openPickupsModal, setOpenPickupsModal] = useState(false);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [filters, setFilters] = useState<IInventoryHistoryFilters>({
    inventoryStatus: [],
    status: [],
    startDate: '',
    endDate: '',
  });
  const [isClearPending, startClearTransition] = useTransition();
  const [isApplyPending, startApplyTransition] = useTransition();
  const [expandedRows, setExpandedRows] = useState<Record<string, LotBreakdownRowState>>({});

  const { profile } = useSelector((state: RootState) => state.parcel);
  const apiKey = profile.data?.credentials.legacyApiKey;
  const country = profile.data?.country;

  const pagination = useServerPagination();

  /**
   * Updates a single order row in the local state without re-fetching the entire list.
   * Called by the polling hook when a specific order's pickup status changes.
   */
  const updateOrderInState = useCallback((updatedOrder: ArrivalOrder) => {
    setOrders((prev) => ({
      ...prev,
      data: prev.data.map((o) => (o._id && o._id === updatedOrder._id ? updatedOrder : o)),
    }));
  }, []);

  const { registerOrders } = useArrivalOrderPolling({ onOrderUpdated: updateOrderInState });

  /**
   * Hook for asynchronous PO label download.
   * Uses polling on /labels/po/async to avoid timeouts on orders with 500+ packages.
   */
  const { downloadLabelsAsync } = usePOLabelAsync({ apiKey: apiKey ?? '' });

  // querys

  const getOrders = useCallback(
    async ({ status, inventoryStatus /*, startDate, endDate */ }: IInventoryHistoryFilters) => {
      const http = initializeHttpProvider();
      try {
        setOrders((prev) => ({ ...prev, loading: true }));
        const response = await http.historyOrder.orders({
          status: convertArrayToQueryString(status!, 'status'),
          inventoryStatus: convertArrayToQueryString(inventoryStatus!, 'inventoryStatus'),
          page: pagination.filters.page ? pagination.filters.page + 1 : 1,
          page_size: pagination.filters.page_size,
          search: search.length > 0 ? search : undefined,
          lot_detail: true,
          // TODO: Descomentar cuando el backend esté listo para filtros de fecha
          // startDate: startDate || undefined,
          // endDate: endDate || undefined,
        });
        setOrders({
          data: response.orders,
          loading: false,
          error: false,
          totalRows: response.metadata.total_records,
        });

        // Register orders for polling — only those with async pickup processing will be polled
        registerOrders(response.orders);
      } catch (error) {
        setOrders((prev) => ({ ...prev, loading: false, error: true }));
        console.error(error);
      }
    },
    [pagination.filters, search, registerOrders],
  );

  const downloadLabels = (arrivalOrderNumber: string) => {
    void downloadLabelsAsync(arrivalOrderNumber);
  };
  const downloadSkuLabels = async (legacyId: string) => {
    const http = initializeHttpProvider();
    toast.promise(
      async () => {
        const response = await http.historyOrder.skuLabels({ legacyId, apiKey: apiKey! });
        if (!response.data) throw new Error('Error al descargar las etiquetas');
        getPDF(response.data);
      },
      {
        pending: 'Descargando etiquetas...',
        success: 'Descarga exitosa',
        error: 'Ha ocurrido un error al intentar descargar las etiquetas',
      },
    );
  };

  const downloadResume = async (arrivalOrderNumber: string) => {
    const http = initializeHttpProvider();
    toast.promise(
      async () => {
        const response = await http.historyOrder.resumePO({ arrivalOrderNumber, apiKey: apiKey! });
        if (!response.data) throw new Error('Error al descargar el resumen');
        getPDF(response.data);
      },
      {
        pending: 'Descargando resumen...',
        success: 'Descarga exitosa',
        error: 'Ha ocurrido un error al intentar descargar el resumen',
      },
    );
  };

  const downloadReport = async (createdAtStart: string, createdAtEnd: string) => {
    const newWindow = window.open('', '_blank');
    toast.promise(
      async () => {
        try {
          const http = initializeHttpProvider();
          const response = await http.statusOrder.downloadOrders({
            report_type: 'arrival_orders_list',
            'filters[createdAtStart]': createdAtStart,
            'filters[createdAtEnd]': createdAtEnd,
            filters: '',
          });
          const url = response.data || response.file_url;
          if (!url) throw new Error('Error al descargar el reporte');
          if (newWindow) {
            newWindow.location.href = url;
          } else {
            window.open(url, '_blank');
          }
        } catch (error) {
          newWindow?.close();
          throw error;
        }
      },
      {
        pending: 'Descargando reporte...',
        success: 'Descarga exitosa',
        error: 'Ha ocurrido un error al intentar descargar el reporte',
      },
    );
    setOpenDownloadModal(false);
  };

  // fin de querys

  // Filtros

  const clearFilters = useCallback(async () => {
    const cleanFilters: IInventoryHistoryFilters = {
      inventoryStatus: [],
      status: [],
      startDate: '',
      endDate: '',
    };
    setFilters(cleanFilters);
    startClearTransition(async () => {
      await getOrders(cleanFilters);
      setOpenDrawer(false);
    });
  }, [getOrders]);

  const applyFilters = useCallback(() => {
    startApplyTransition(async () => {
      await getOrders({ ...filters });
      setOpenDrawer(false);
    });
  }, [filters, getOrders]);

  const onChangeFilter = useCallback(
    (filterName: keyof IInventoryHistoryFilters, value: string) => {
      if (filterName === 'startDate' || filterName === 'endDate') {
        setFilters({ ...filters, [filterName]: value });
      } else {
        const currentValues = (filters[filterName] as string[]) || [];
        const newValue = currentValues?.includes(value)
          ? currentValues.filter((item) => item !== value)
          : [...currentValues, value];
        setFilters({ ...filters, [filterName]: newValue });
      }
    },
    [filters, setFilters],
  );

  const fetchOrdersWithFilters = useCallback(async () => {
    void getOrders({
      inventoryStatus: filters.inventoryStatus,
      status: filters.status,
      // TODO: Descomentar cuando el backend esté listo para filtros de fecha
      startDate: '', // filters.startDate,
      endDate: '', // filters.endDate,
    });
  }, [filters, getOrders]);

  const onChgSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      if (value.length >= 5) {
        setSearch(value);
      }
      if (value.length === 0) {
        setSearch('');
        void fetchOrdersWithFilters();
      }
    },
    [fetchOrdersWithFilters],
  );

  const onSearch = useCallback(() => {
    void fetchOrdersWithFilters();
    pagination.onChangeFilter({ page: 0 });
  }, [fetchOrdersWithFilters, pagination]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  // fin filtros

  // handlers

  const handleOpenDrawer = () => {
    setOpenDrawer(!openDrawer);
  };

  const toggleLotBreakdown = useCallback((order: ArrivalOrder) => {
    const id = order.aoNumber;
    setExpandedRows((prev) => {
      if (!prev[id]) return { ...prev, [id]: 'open' };
      if (prev[id] === 'open') return { ...prev, [id]: 'closing' };
      return prev;
    });
  }, []);

  const finalizeLotCollapse = useCallback((order: ArrivalOrder) => {
    const id = order.aoNumber;
    setExpandedRows((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const onShowPickups = (order: ArrivalOrder) => {
    setSelectedOrder(order);
    setOpenPickupsModal(true);
  };

  const handleClosePickupsModal = () => {
    setOpenPickupsModal(false);
    setSelectedOrder(null);
  };

  const hasActiveFilters = (): boolean => {
    const { status, inventoryStatus /*, startDate, endDate */ } = filters;
    return (
      (status && status.length > 0) ||
      (inventoryStatus && inventoryStatus.length > 0) ||
      // TODO: Descomentar cuando el backend esté listo para filtros de fecha
      // (startDate && startDate.length > 0) ||
      // (endDate && endDate.length > 0) ||
      search.length >= 5
    );
  };

  // fin handlers

  useEffect(() => {
    void getOrders({
      inventoryStatus: [],
      status: [],
      startDate: '',
      endDate: '',
    });
  }, [getOrders]);

  return {
    search,
    openDrawer,
    filters,
    orders,
    selectedOrder,
    openPickupsModal,
    handleOpenDrawer,
    clearFilters,
    onChgSearch,
    applyFilters,
    onChangeFilter,
    onSearch,
    handleKeyPress,
    pagination,
    downloadLabels,
    downloadSkuLabels,
    downloadResume,
    onShowPickups,
    handleClosePickupsModal,
    hasActiveFilters,
    isClearPending,
    isApplyPending,
    openDownloadModal,
    setOpenDownloadModal,
    country,
    expandedRows,
    toggleLotBreakdown,
    finalizeLotCollapse,
    downloadReport,
  };
};

export default useInventoryHistory;
